import { useState, useEffect, useCallback } from "react";
import {
    advanceTimeAllowedValues,
    DEFAULT_MIN_ADVANCE_TIME,
    DEFAULT_MAX_ADVANCE_TIME,
} from "../constants/SendConfigConstants";

interface UseAdvanceTimeSliderParams {
    minAdvanceTime?: number;
    maxAdvanceTime?: number;
    onAdvanceTimeChange: (minAdvanceTime: number, maxAdvanceTime: number) => void;
    minGapMinutes?: number;
}

interface UseAdvanceTimeSliderReturn {
    advanceTimeRange: [number, number]; // Still returns minute values
    sliderIndexRange: [number, number]; // New: indices for slider position
    handleAdvanceTimeChange: (newIndexValue: [number, number]) => void;
    handleAdvanceTimeChangeEnd: (newIndexValue: [number, number]) => void;
    formatAdvanceTimeLabel: (value: number) => string;
    minIndex: number;
    maxIndex: number;
}

const DEFAULT_MIN_GAP_MINUTES = 60; // 1 hour
const DEFAULT_ADVANCE_TIME_RANGE: [number, number] = [DEFAULT_MIN_ADVANCE_TIME, DEFAULT_MAX_ADVANCE_TIME];
const MIN_SLIDER_VALUE = DEFAULT_MIN_ADVANCE_TIME;
const MAX_SLIDER_VALUE = 14400; // 10 days

// Use indices for uniform spacing (0-15 for 16 values)
const MIN_INDEX = 0;
const MAX_INDEX = advanceTimeAllowedValues.length - 1;

/**
 * Converts a slider index to the actual minute value
 */
function indexToValue(index: number): number {
    const clampedIndex = Math.round(Math.max(MIN_INDEX, Math.min(MAX_INDEX, index)));
    return advanceTimeAllowedValues[clampedIndex];
}

/**
 * Converts a minute value to its corresponding slider index
 * For values not in advanceTimeAllowedValues, rounds UP to preserve user intent
 * (e.g., legacy 4320min/3d becomes 5760min/4d instead of 2880min/2d)
 */
function valueToIndex(value: number): number {
    const index = advanceTimeAllowedValues.indexOf(value);
    if (index !== -1) return index;

    // If exact value not found, find next higher value (round up)
    // This prevents data loss when migrating legacy alerts
    for (let i = 0; i < advanceTimeAllowedValues.length; i++) {
        if (advanceTimeAllowedValues[i] >= value) {
            return i;
        }
    }

    // If value exceeds max, return max index
    return MAX_INDEX;
}

/**
 * Custom hook to manage advance time range slider state and business logic
 * Handles minute-to-label conversion, validation, and state synchronization
 */
export function useAdvanceTimeSlider({
    minAdvanceTime,
    maxAdvanceTime,
    onAdvanceTimeChange,
    minGapMinutes = DEFAULT_MIN_GAP_MINUTES,
}: UseAdvanceTimeSliderParams): UseAdvanceTimeSliderReturn {
    // Initialize with provided values if available and valid, otherwise use defaults
    const getInitialMinAdvanceTime = () => {
        if (minAdvanceTime !== undefined && minAdvanceTime >= MIN_SLIDER_VALUE && minAdvanceTime <= MAX_SLIDER_VALUE) {
            return minAdvanceTime;
        }
        return DEFAULT_ADVANCE_TIME_RANGE[0];
    };

    const getInitialMaxAdvanceTime = () => {
        if (maxAdvanceTime !== undefined && maxAdvanceTime >= MIN_SLIDER_VALUE && maxAdvanceTime <= MAX_SLIDER_VALUE) {
            return maxAdvanceTime;
        }
        return DEFAULT_ADVANCE_TIME_RANGE[1];
    };

    const [advanceTimeRange, setAdvanceTimeRange] = useState<[number, number]>(() => [
        getInitialMinAdvanceTime(),
        getInitialMaxAdvanceTime(),
    ]);

    // Slider works with indices for uniform spacing
    const [sliderIndexRange, setSliderIndexRange] = useState<[number, number]>(() => [
        valueToIndex(getInitialMinAdvanceTime()),
        valueToIndex(getInitialMaxAdvanceTime()),
    ]);

    /**
     * Formats minute value to human-readable label
     * Uses exact day boundaries to match slider marks precisely
     * 15 → "15min", 60 → "1h", 120 → "2h", 1440 → "1d", etc.
     */
    const formatAdvanceTimeLabel = useCallback((minutes: number): string => {
        if (minutes < 60) {
            return `${minutes}min`;
        } else if (minutes < 1440) {
            const hours = Math.floor(minutes / 60);
            return `${hours}h`;
        } else {
            // Calculate exact days - no rounding
            // Each day = 1440 minutes exactly
            const days = Math.floor(minutes / 1440);
            return `${days}d`;
        }
    }, []);

    /**
     * Validates if the time range meets minimum gap requirements
     */
    const isValidRange = useCallback(
        (min: number, max: number): boolean => {
            const rangeDifference = max - min;
            return rangeDifference >= minGapMinutes;
        },
        [minGapMinutes]
    );

    /**
     * Initialize slider from external values when they change
     */
    useEffect(() => {
        if (
            minAdvanceTime !== undefined &&
            maxAdvanceTime !== undefined &&
            minAdvanceTime >= MIN_SLIDER_VALUE &&
            maxAdvanceTime <= MAX_SLIDER_VALUE
        ) {
            setAdvanceTimeRange([minAdvanceTime, maxAdvanceTime]);
            setSliderIndexRange([
                valueToIndex(minAdvanceTime),
                valueToIndex(maxAdvanceTime),
            ]);
        }
    }, [minAdvanceTime, maxAdvanceTime]);

    /**
     * Handle slider value change during drag (local state only)
     * Receives index values and converts to minute values
     */
    const handleAdvanceTimeChange = useCallback(
        (newIndexValue: [number, number]) => {
            const [minIndex, maxIndex] = newIndexValue;

            // Convert indices to actual minute values
            const minValue = indexToValue(minIndex);
            const maxValue = indexToValue(maxIndex);

            // Prevent range less than minimum gap
            if (!isValidRange(minValue, maxValue)) {
                return;
            }

            setSliderIndexRange([minIndex, maxIndex]);
            setAdvanceTimeRange([minValue, maxValue]);
        },
        [isValidRange]
    );

    /**
     * Handle slider value change when drag ends (notify parent)
     * Receives index values and converts to minute values
     */
    const handleAdvanceTimeChangeEnd = useCallback(
        (newIndexValue: [number, number]) => {
            const [minIndex, maxIndex] = newIndexValue;

            // Convert indices to actual minute values
            const minValue = indexToValue(minIndex);
            const maxValue = indexToValue(maxIndex);

            // Validate before notifying parent
            if (!isValidRange(minValue, maxValue)) {
                return;
            }

            setSliderIndexRange([minIndex, maxIndex]);
            setAdvanceTimeRange([minValue, maxValue]);
            onAdvanceTimeChange(minValue, maxValue);
        },
        [isValidRange, onAdvanceTimeChange]
    );

    return {
        advanceTimeRange, // Minute values for display
        sliderIndexRange, // Index values for slider position
        handleAdvanceTimeChange,
        handleAdvanceTimeChangeEnd,
        formatAdvanceTimeLabel,
        minIndex: MIN_INDEX,
        maxIndex: MAX_INDEX,
    };
}
